
import { useCallback, useState } from 'react';
import { checkMessageLimit, incrementMessageUsage } from '@/services/ai-message-limits-service';
import { v4 as uuidv4 } from 'uuid';

interface UseUsageValidationProps {
  userId: string | null;
  onError?: (error: Error, context?: any) => void;
  onUsageLimitError?: (error: Error) => boolean;
  onUsageUpdate?: () => void;
}

interface UsageValidationResult {
  allowed: boolean;
  reason?: string;
  daily_limit: number;
  monthly_limit: number;
  daily_usage: number;
  monthly_usage: number;
  package: string;
}

export const useUsageValidation = ({ 
  userId, 
  onError, 
  onUsageLimitError, 
  onUsageUpdate 
}: UseUsageValidationProps) => {
  
  const [usageUpdateTrigger, setUsageUpdateTrigger] = useState<number>(0);
  
  const validateAndIncrementUsage = useCallback(async (
    topic: 'guide' | 'performance' | 'trading_strategy' | 'advisor',
    requestId?: string
  ): Promise<UsageValidationResult> => {
    const finalRequestId = requestId || uuidv4();
    
    try {
      console.log('[USAGE VALIDATION] Checking subscription limits:', {
        userId: userId?.substring(0, 8) + '...',
        topic,
        requestId: finalRequestId
      });

      if (!userId) {
        throw new Error('User ID is required for usage validation');
      }

      // Check real subscription limits
      const limitCheck = await checkMessageLimit(userId, topic, finalRequestId);
      
      if (!limitCheck.allowed) {
        console.warn('[USAGE VALIDATION] Usage limit exceeded:', {
          reason: limitCheck.reason,
          dailyUsage: `${limitCheck.daily_usage}/${limitCheck.daily_limit}`,
          monthlyUsage: `${limitCheck.monthly_usage}/${limitCheck.monthly_limit}`,
          package: limitCheck.package
        });

        // Handle limit exceeded error
        const error = new Error(`Usage limit exceeded: ${limitCheck.reason}`);
        if (onUsageLimitError) {
          const handled = onUsageLimitError(error);
          if (handled) {
            // If error was handled (e.g., upgrade prompt shown), don't throw
            console.log('[USAGE VALIDATION] Limit error handled by callback');
          }
        }

        // Return the limit check result as-is (not allowed)
        const result = {
          allowed: limitCheck.allowed,
          reason: limitCheck.reason,
          daily_limit: limitCheck.daily_limit,
          monthly_limit: limitCheck.monthly_limit,
          daily_usage: limitCheck.daily_usage,
          monthly_usage: limitCheck.monthly_usage,
          package: limitCheck.package
        };

        return result;
      }

      // Increment usage counter
      const incrementSuccess = await incrementMessageUsage(userId, topic, finalRequestId);
      
      if (!incrementSuccess) {
        throw new Error('Failed to record usage');
      }

      // Trigger usage update callback
      if (onUsageUpdate) {
        onUsageUpdate();
      }
      
      // Trigger local state update
      setUsageUpdateTrigger(prev => prev + 1);

      const finalResult = {
        allowed: limitCheck.allowed,
        daily_limit: limitCheck.daily_limit,
        monthly_limit: limitCheck.monthly_limit,
        daily_usage: limitCheck.daily_usage + 1, // Optimistically increment
        monthly_usage: limitCheck.monthly_usage + 1, // Optimistically increment
        package: limitCheck.package
      };

      console.log('[USAGE VALIDATION] Access granted and usage recorded:', {
        requestId: finalRequestId,
        topic,
        result: finalResult
      });

      return finalResult;

    } catch (error) {
      console.error('[USAGE VALIDATION] Error during validation:', error);

      // Handle errors appropriately
      if (onError) {
        onError(error instanceof Error ? error : new Error(String(error)), {
          topic,
          requestId: finalRequestId,
          userId
        });
      }

      // Return error state (not allowed)
      return {
        allowed: false,
        reason: 'no_limits_configured',
        daily_limit: 1,
        monthly_limit: 20,
        daily_usage: 0,
        monthly_usage: 0,
        package: 'free'
      };
    }
  }, [userId, onError, onUsageLimitError, onUsageUpdate]);

  return {
    validateAndIncrementUsage,
    usageUpdateTrigger
  };
};
